import React, { useState, forwardRef } from 'react';
import { isNil } from 'lodash';
import { Box } from '@fiverr-private/theme';
import { findChildByType } from '@fiverr-private/ui_utils';
import type { TagProps } from './types';
import { getElement } from './utils';
import { getTagStyles } from './styles';
import { TagContext } from './TagContext';

const Tag = forwardRef<HTMLElement, TagProps>(
  (
    {
      size = 'md',
      variant = 'filled',
      onClick,
      href,
      children,
      target,
      ariaLabel,
      hideOnClick,
      onKeyDown,
      tabIndex,
      maxWidth,
      clickableArea = 'tag',
      shape = 'default',
      goExperimental = false,
      theme = 'light',
      dataTrackTag = 'tag',
      dataTrackValue,
    }: TagProps,
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(true);
    const hasClickEvent = Boolean(onClick || hideOnClick);
    const hasCloseIcon = !!findChildByType(children, 'TagCloseIcon');
    const isDark = goExperimental || theme === 'dark';

    const onTagClick = (e) => {
      if (hideOnClick && clickableArea === 'tag') {
        setIsOpen(false);
      }

      onClick?.(e);
    };

    const { element, role, tabIndex: elementTabIndex } = getElement(href, hasClickEvent, clickableArea);
    const isInteractive = Boolean(role);

    const props = {
      onClick: hasClickEvent ? onTagClick : undefined,
      href,
      role,
      target,
      'aria-label': ariaLabel,
      onKeyDown,
      tabIndex: !isNil(elementTabIndex) ? elementTabIndex : tabIndex,
    };

    if (!isOpen) {
      return null;
    }
    return (
      <TagContext.Provider
        value={{
          setIsOpen,
          clickableArea,
          variant,
          hideOnClick,
          size,
          isDark,
        }}
      >
        <Box
          {...getTagStyles({ size, isInteractive, variant, hasCloseIcon, shape, isDark })}
          as={element}
          ref={ref}
          maxWidth={maxWidth}
          {...props}
          dataTrackTag={dataTrackTag}
          dataTrackValue={dataTrackValue}
        >
          {children}
        </Box>
      </TagContext.Provider>
    );
  }
);

Tag.displayName = 'Tag';

export default Tag;
